import * as React from 'react';
import { Page } from '../../subs/Page';

import { useParams } from 'react-router-dom';
import { WorkshopUser, getWorkshopUsers } from '../../types/WorkshopUser';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import { DataGrid, GridRowEditStopReasons, GridColDef } from '@mui/x-data-grid';
import { GridRowModesModel, GridRowModes, GridActionsCellItem, GridEventListener, GridRowId } from '@mui/x-data-grid';
import { User } from '../../UsersPage';
import { ApiResponse } from '../../types/ApiResponse';
import { getUsers } from '../../types/User';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { formatDateString, httpPost } from '../../Web';
import { UserContext } from '../../UserProvider';

const renderCellFunction = (formattedValue?: string) => {
    return <Typography variant='body1'>{formattedValue ?? ''}</Typography>
}

const dateToDBString = (date?: Date | null) => {
    if (!date) {
        return null;
    }

    const paddingZeros = '0'.repeat(6);
    return dayjs(date).format(`YYYYMMDD[${paddingZeros}]`);
}

const dbDateToJsDate = (date?: string | null) => {
    const dateStr = formatDateString(date ?? undefined);
    return dateStr ? new Date(dateStr) : undefined;
}

const formatDisplayName = (fullName?: string) => {
    const source = fullName ?? '';
    const [firstName, ...rest] = source.split(' ', 2);
    const lastName = rest.join().trim();
    return `${lastName}, ${firstName}`;
}

const formatDisplayDate = (date?: Date) => {
    return date ? dayjs(date).format('YYYY-MM-DD') : "";
}

type ArrivalRow = {
    id: number;
    user: User;
    workshopUser: WorkshopUser;
    name: string;
    hotel: string;
    arrivalTime?: Date;
    departureTime?: Date;
}

export const ArrivalInformation = () => {
    const userContext = React.useContext(UserContext);
    const currentUserId = (() => {
        const contextValue = userContext as Record<string, unknown> | null;
        if (!contextValue || !("user" in contextValue)) {
            return undefined;
        }
        const user = contextValue.user as { id?: number } | undefined;
        return user?.id;
    })();
    const { wsId } = useParams();
    const [rows, setAllRows] = React.useState<ArrivalRow[]>([]);
    const [dataLoading, setDataLoading] = React.useState<boolean>(true);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

    React.useEffect(() => {
        const loadUsers = async () => {
            try {
                const { failed, result: wsUsers }: ApiResponse<WorkshopUser[]> = await getWorkshopUsers(Number(wsId), new AbortController());
                const loadedRows: ArrivalRow[] = [];

                if (!failed) {
                    const userResponses = await getUsers(wsUsers.map(wu => wu.userId));

                    userResponses.forEach(userResponse => {
                        const userId = userResponse.result.id;
                        if (userId === undefined) {
                            return;
                        }

                        const wsUser = wsUsers.find(wsu => wsu.userId === userId);
                        if (!wsUser) {
                            return;
                        }

                        loadedRows.push({
                            id: userId,
                            user: userResponse.result,
                            workshopUser: wsUser,
                            name: userResponse.result.name,
                            hotel: wsUser.hotel ?? "",
                            arrivalTime: dbDateToJsDate(wsUser.arrivalTime),
                            departureTime: dbDateToJsDate(wsUser.departureTime)
                        });
                    });

                    setAllRows(loadedRows);
                    setDataLoading(false);
                    console.log('Arrival rows: ', loadedRows);
                }
            } catch (error) {
                Promise.reject(error);
            }
        }

        if (wsId)
            loadUsers();
    }, [wsId]);

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        if (currentUserId !== id) {
            return;
        }
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const columns: GridColDef<ArrivalRow>[] = [
        {
            field: 'name',
            headerName: 'Last name, First name',
            renderCell: (params) => renderCellFunction(params.formattedValue as string | undefined),
            valueGetter: (params: string) => {
                const fullName = params ?? '';
                const [firstName, ...rest] = fullName.split(' ', 2);
                const lastName = rest.join().trim();
                return `${lastName}, ${firstName}`;
            },
            flex: 2,
        },
        {
            field: 'hotel',
            headerName: 'Hotel',
            editable: true,
            renderCell: (params) => renderCellFunction(params.formattedValue as string | undefined),
            flex: 3,
        },
        {
            field: 'arrivalTime',
            headerName: 'Arrival Time',
            type: 'date',
            editable: true,
            renderCell: (params) => renderCellFunction(params.formattedValue as string | undefined),
            valueFormatter: (params: Date | undefined) => {
                return params ? dayjs(params).format('YYYY-MM-DD') : "";
            },
            flex: 1,
        },
        {
            field: 'departureTime',
            headerName: 'Departure Time',
            type: 'date',
            editable: true,
            renderCell: (params) => renderCellFunction(params.formattedValue as string | undefined),
            valueFormatter: (params: Date | undefined) => {
                return params ? dayjs(params).format('YYYY-MM-DD') : "";
            },
            flex: 1
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            disabled={currentUserId !== id}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        disabled={currentUserId !== id}
                        color="inherit"
                    />,
                ];
            },
        },
    ]

    const submitWorkshopUser = async(workshopUser: WorkshopUser) => {
        return httpPost('/api/workshop/user/update', workshopUser, new AbortController());
    }

    return (
        <Page
            backButton
            heading={'View / change arrival information'}
            children={[
                <div key='arrival-information-grid' style={{ display: 'flex', flexDirection: 'column' }}>
                    <DataGrid columns={columns}
                        rows={rows}
                        getRowClassName={(params)=>params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even':'Mui-odd'}
                        editMode='row'
                        onRowEditStart={(_params, _event, details)=>{
                            console.log('Row edit start: ', _params, details)
                        }}

                        rowModesModel={rowModesModel}
                        columnVisibilityModel={{ id: true }}
                        sx={{
                            "& .selectRowClass": {
                                color: 'primary.main'
                            },
                        }}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        slotProps={{
                            toolbar: { setAllRows, setRowModesModel },
                        }}

                        processRowUpdate={async (newRow, oldRow) => {
                            const updatedWorkshopUser: WorkshopUser = {
                                ...newRow.workshopUser,
                                hotel: newRow.hotel ?? "",
                                arrivalTime: dateToDBString(newRow.arrivalTime),
                                departureTime: dateToDBString(newRow.departureTime)
                            };

                            try {
                                const readback: ApiResponse<WorkshopUser> = await submitWorkshopUser(updatedWorkshopUser);

                                if (readback.failed || !readback.result) {
                                    console.log('readback: ', readback);
                                    return oldRow;
                                }

                                const updatedRow: ArrivalRow = {
                                    ...newRow,
                                    workshopUser: readback.result,
                                    hotel: readback.result.hotel ?? "",
                                    arrivalTime: dbDateToJsDate(readback.result.arrivalTime),
                                    departureTime: dbDateToJsDate(readback.result.departureTime)
                                };

                                setAllRows(prev => prev.map(row => row.id === updatedRow.id ? updatedRow : row));

                                return updatedRow;
                            } catch (error) {
                                console.log('submitWorkshopUser failed: ', error);
                                return oldRow;
                            }
                        }}
                        showCellVerticalBorder
                        disableColumnMenu
                        getRowId={(param) => param.id}
                        hideFooter
                        loading={dataLoading}
                    />
                </div>
            ]}
        />
    );
}